export type PostHogErrorSeverity = 'warning' | 'error' | 'fatal';
export type PostHogErrorCategory =
  | 'access'
  | 'application'
  | 'configuration'
  | 'dependency'
  | 'resource'
  | 'user_action';

export type PostHogErrorClassification = {
  category: PostHogErrorCategory;
  code: string;
  expected: boolean;
  retryable: boolean;
  severity: PostHogErrorSeverity;
};
type UnknownRecord = Record<string, unknown>;
type ClassifyPostHogErrorArgs = {
  errorName?: string;
  message: string;
};
type BuildPostHogErrorMetadataArgs = ClassifyPostHogErrorArgs & {
  errorCode?: string;
  expected?: boolean;
  handled?: boolean;
  operation?: string;
  retryable?: boolean;
  severity?: PostHogErrorSeverity;
  topApplicationFrame?: string;
};
type BuildPostHogExceptionListArgs = {
  errorMessage: string;
  errorName?: string;
  errorStack?: string;
  handled?: boolean;
};
const CREDENTIAL_PATTERN =
  /\b(?:bearer\s+)?(?:phc|phx|sk|pk|rk)_[A-Za-z0-9_-]{12,}\b/gi;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const OPAQUE_CONTEXT_ID_PATTERN =
  /\b(message|stream|thread|job|asset|request|document(?:\s+ID)?)\s+([a-z0-9]{24,})\b/gi;
const PHONE_PATTERN =
  /(^|[^A-Za-z0-9])(\+?\d[\d ().-]{8,18}\d)(?![\dA-Za-z:])/g;
const URL_PATTERN = /https?:\/\/[^\s"'<>]+/gi;
const UUID_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;
const WORKSPACE_PATH_PATTERN =
  /\/[A-Za-z0-9_-]+\/users\/@[A-Za-z0-9_-]+\/[^\s"']+/g;
const SENSITIVE_PROPERTY_SEGMENTS = new Set([
  'authorization',
  'body',
  'content',
  'cookie',
  'email',
  'file_name',
  'filename',
  'header',
  'headers',
  'message',
  'password',
  'path',
  'phone',
  'prompt',
  'secret',
  'slug',
  'token',
]);
const SAFE_OPAQUE_CORRELATION_PROPERTY_PATTERN =
  /^(?:organization_id|org_thread_db_id|org_thread_ref|request_id|thread_id)$/;
const CLASSIFICATION_RULES: Array<{
  classification: PostHogErrorClassification;
  pattern: RegExp;
}> = [
  {
    pattern: /FAL_ADMIN_KEY|falPayloadCleanup.*required/i,
    classification: {
      category: 'configuration',
      code: 'FAL_CLEANUP_CONFIGURATION_REQUIRED',
      expected: false,
      retryable: false,
      severity: 'fatal',
    },
  },
  {
    pattern: /FAL cleanup failed \(404\)|FAL cleanup.*request not found/i,
    classification: {
      category: 'dependency',
      code: 'FAL_CLEANUP_REQUEST_NOT_FOUND',
      expected: true,
      retryable: false,
      severity: 'warning',
    },
  },
  {
    pattern: /\bAUTH_REQUIRED\b|authentication required|not authenticated/i,
    classification: {
      category: 'access',
      code: 'AUTH_REQUIRED',
      expected: true,
      retryable: false,
      severity: 'warning',
    },
  },
  {
    pattern: /\bUNAUTHORIZED\b|\bFORBIDDEN\b|access denied/i,
    classification: {
      category: 'access',
      code: 'FORBIDDEN',
      expected: true,
      retryable: false,
      severity: 'warning',
    },
  },
  {
    pattern: /INSUFFICIENT_CREDITS|not enough credits|insufficient credits/i,
    classification: {
      category: 'resource',
      code: 'INSUFFICIENT_CREDITS',
      expected: true,
      retryable: false,
      severity: 'warning',
    },
  },
  {
    pattern: /ResizeObserver loop (?:limit exceeded|completed)/i,
    classification: {
      category: 'application',
      code: 'RESIZE_OBSERVER_LOOP',
      expected: true,
      retryable: false,
      severity: 'warning',
    },
  },
  {
    pattern: /cancell?ed by (?:the )?user|workflow was cancell?ed/i,
    classification: {
      category: 'user_action',
      code: 'USER_CANCELED',
      expected: true,
      retryable: false,
      severity: 'warning',
    },
  },
  {
    pattern: /Minified React error #185|maximum update depth exceeded/i,
    classification: {
      category: 'application',
      code: 'REACT_MAXIMUM_UPDATE_DEPTH',
      expected: false,
      retryable: false,
      severity: 'error',
    },
  },
  {
    pattern: /InvalidCursor|cursor is from a different query/i,
    classification: {
      category: 'application',
      code: 'PAGINATION_CURSOR_MISMATCH',
      expected: false,
      retryable: false,
      severity: 'error',
    },
  },
  {
    pattern: /update on nonexistent document/i,
    classification: {
      category: 'application',
      code: 'STALE_DOCUMENT_WRITE',
      expected: false,
      retryable: true,
      severity: 'error',
    },
  },
  {
    pattern:
      /fetch failed|service temporarily unavailable|timed? ?out|\b429\b|\b503\b/i,
    classification: {
      category: 'dependency',
      code: 'DEPENDENCY_UNAVAILABLE',
      expected: false,
      retryable: true,
      severity: 'error',
    },
  },
];

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizePropertyKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/^\$+/, '')
    .toLowerCase();
}

function isSensitivePropertyKey(key: string): boolean {
  const normalized = normalizePropertyKey(key);
  if (
    normalized === 'workspace_storage_slug' ||
    normalized === 'organization_name' ||
    normalized === 'user_name'
  ) {
    return true;
  }

  return normalized
    .split(/[^a-z0-9]+/)
    .some((segment) => SENSITIVE_PROPERTY_SEGMENTS.has(segment));
}

function redactUrl(value: string): string {
  try {
    const url = new URL(value);
    return `${url.origin}/[redacted]`;
  } catch {
    return '[redacted-url]';
  }
}

function sanitizePropertyValue(
  key: string,
  value: unknown,
  depth: number
): unknown {
  if (isSensitivePropertyKey(key)) {
    const normalized = normalizePropertyKey(key);
    if (normalized.endsWith('url') && typeof value === 'string') {
      return redactUrl(value);
    }
    if (normalized.endsWith('message') && typeof value === 'string') {
      return redactPostHogErrorMessage(value);
    }
    if (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      /(?:^|_)(?:count|length|size)$/.test(normalized)
    ) {
      return value;
    }
    return '[redacted]';
  }
  const normalized = normalizePropertyKey(key);
  if (
    typeof value === 'string' &&
    SAFE_OPAQUE_CORRELATION_PROPERTY_PATTERN.test(normalized) &&
    /^[A-Za-z0-9:_-]{1,200}$/.test(value)
  ) {
    return value;
  }
  if (typeof value === 'string') {
    return redactPostHogErrorMessage(value);
  }
  if (Array.isArray(value)) {
    if (depth >= 3) return '[truncated]';
    return value
      .slice(0, 50)
      .map((item) =>
        isRecord(item)
          ? sanitizePostHogErrorProperties(item, depth + 1)
          : typeof item === 'string'
            ? redactPostHogErrorMessage(item)
            : item
      );
  }
  if (isRecord(value)) {
    if (depth >= 3) return '[truncated]';
    return sanitizePostHogErrorProperties(value, depth + 1);
  }
  return value;
}
function normalizeFingerprintPart(value: string | undefined): string {
  const normalized = (value ?? 'unknown')
    .replace(/\\/g, '/')
    .replace(/:\d+(?::\d+)?$/g, '')
    .replace(/[^A-Za-z0-9_./-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return normalized || 'unknown';
}
function normalizeExplicitCode(value: string): string {
  const normalized = value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
  return normalized || 'UNCLASSIFIED_EXCEPTION';
}

export function classifyPostHogError({
  errorName,
  message,
}: ClassifyPostHogErrorArgs): PostHogErrorClassification {
  const searchable = `${errorName ?? ''}\n${message}`;
  const matched = CLASSIFICATION_RULES.find(({ pattern }) =>
    pattern.test(searchable)
  );
  if (matched) return matched.classification;

  const embeddedCode = searchable.match(
    /\b([A-Z][A-Z0-9]+(?:_[A-Z0-9]+)+)\b/
  )?.[1];

  return {
    category: 'application',
    code: embeddedCode ?? normalizeExplicitCode(errorName ?? 'Error'),
    expected: false,
    retryable: false,
    severity: 'error',
  };
}
export function redactPostHogErrorMessage(message: string): string {
  return message
    .replace(CREDENTIAL_PATTERN, '[credential]')
    .replace(EMAIL_PATTERN, '[email]')
    .replace(URL_PATTERN, (value) => redactUrl(value))
    .replace(UUID_PATTERN, '[uuid]')
    .replace(WORKSPACE_PATH_PATTERN, '[workspace-path]')
    .replace(
      OPAQUE_CONTEXT_ID_PATTERN,
      (_match, label: string) => `${label} [opaque-id]`
    )
    .replace(PHONE_PATTERN, (match, prefix: string, phone: string) => {
      const digitCount = phone.replace(/\D/g, '').length;
      return digitCount >= 10 && digitCount <= 15 ? `${prefix}[phone]` : match;
    })
    .slice(0, 2_000);
}

export function sanitizePostHogErrorProperties(
  properties: UnknownRecord,
  depth = 0,
  maxCustomProperties = 100
): UnknownRecord {
  let customPropertyCount = 0;
  return Object.fromEntries(
    Object.entries(properties)
      .filter(
        ([key]) =>
          key.startsWith('$') || customPropertyCount++ < maxCustomProperties
      )
      .map(([key, value]) => [key, sanitizePropertyValue(key, value, depth)])
  );
}

export function normalizePostHogDistinctId(distinctId: string): string {
  const value = distinctId.trim();
  if (EMAIL_PATTERN.test(value)) {
    EMAIL_PATTERN.lastIndex = 0;
    return 'redacted:contact';
  }
  EMAIL_PATTERN.lastIndex = 0;
  if (/^\+?[\d ()-]{9,}$/.test(value)) return 'redacted:contact';
  return value || 'anonymous';
}

export function buildPostHogErrorMetadata(
  args: BuildPostHogErrorMetadataArgs
): UnknownRecord {
  const classification = classifyPostHogError(args);
  const code = args.errorCode
    ? normalizeExplicitCode(args.errorCode)
    : classification.code;
  const operation = normalizeFingerprintPart(args.operation);
  const topFrame = normalizeFingerprintPart(args.topApplicationFrame);

  return {
    $exception_fingerprint: `imai-v1:${code.toLowerCase()}:${operation}:${topFrame}`,
    $exception_level: args.severity ?? classification.severity,
    error_category: classification.category,
    error_code: code,
    error_expected: args.expected ?? classification.expected,
    error_handled: args.handled ?? false,
    error_operation: operation,
    error_retryable: args.retryable ?? classification.retryable,
    error_schema_version: 1,
    error_top_application_frame: topFrame,
  };
}

export function normalizePostHogStackFilename(filename: string): string {
  const normalized = filename
    .replace(/^webpack:\/\/(?:_N_E\/)?/, '')
    .replace(/^file:\/\//, '')
    .replace(/^.*\/imai\//, '')
    .replace(/^https?:\/\/[^/]+\//, '/')
    .replace(/[?#].*$/, '')
    .replace(/[-.][0-9a-f]{8,}(?=\.[cm]?js$)/gi, '');
  return redactPostHogErrorMessage(normalized);
}

export function parsePostHogStackFrames(
  stack: string | undefined
): UnknownRecord[] {
  if (!stack) return [];

  return stack
    .split('\n')
    .filter((line) => /^at\s+/.test(line.trim()))
    .slice(0, 50)
    .flatMap((line) => {
      const match = line
        .trim()
        .match(/^at\s+(?:(.*?)\s+\()?(.+?):(\d+):(\d+)\)?$/);
      if (!match) return [];
      const [, functionName, rawFilename, lineNumber, columnNumber] = match;
      if (!rawFilename) return [];
      const filename = normalizePostHogStackFilename(rawFilename);
      return [
        {
          filename,
          function: functionName || '<anonymous>',
          lineno: Number(lineNumber),
          colno: Number(columnNumber),
          platform: 'node:javascript',
          in_app:
            !filename.includes('node_modules/') &&
            !filename.startsWith('node:') &&
            !filename.includes('/_next/static/chunks/'),
        },
      ];
    });
}

export function resolvePostHogTopApplicationFrame(
  stack: string | undefined
): string | undefined {
  const frames = parsePostHogStackFrames(stack);
  const frame = frames.find((candidate) => candidate.in_app === true);
  return typeof frame?.filename === 'string' ? frame.filename : undefined;
}

export function buildPostHogExceptionList({
  errorMessage,
  errorName,
  errorStack,
  handled = false,
}: BuildPostHogExceptionListArgs): UnknownRecord[] {
  const frames = parsePostHogStackFrames(errorStack).reverse();
  return [
    {
      type: errorName ?? 'Error',
      value: redactPostHogErrorMessage(errorMessage),
      mechanism: { handled, type: 'generic' },
      ...(frames.length > 0 ? { stacktrace: { frames } } : {}),
    },
  ];
}
